// contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { supabase } from '../services/supabase';

export const AuthContext = createContext(null);

// Define admin emails list
const ADMIN_EMAILS = [
  'skdastro@gmail.com',
  'dipti@contentsimi.com'
  // Add other admin emails here
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = React.memo(({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAdminStatus = async (session) => {
    try {
      const userEmail = session?.user?.email?.toLowerCase();
      const adminStatus = ADMIN_EMAILS.includes(userEmail);
      console.log(`Admin check for ${userEmail}:`, adminStatus);
      setIsAdmin(adminStatus);
    } catch (error) {
      console.error('Admin check failed:', error);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user) {
            await checkAdminStatus(session);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setUser(null);
          setIsAdmin(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;

      setUser(session?.user ?? null);
      if (session?.user) {
        await checkAdminStatus(session);
      } else {
        setIsAdmin(false);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo(() => ({
    signUp: async (data) => {
      const result = await supabase.auth.signUp(data);
      return result;
    },
    signIn: async (data) => {
      const result = await supabase.auth.signInWithPassword(data);
      return result;
    },
    signOut: async () => {
      await supabase.auth.signOut();
      setUser(null);
      setIsAdmin(false);
    },
    user,
    loading,
    isAdmin
  }), [user, loading, isAdmin]);

  if (loading) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});